// src/utils/promoteNote.js
// wppromote-6a3f1e94 — Notes Consolidation Seam 3 (Promote).
//
// Notes are internal. Work Performed is the one customer-facing work text.
// Promoting a note APPENDS its text into a Work Performed box, UNSAVED, so the
// reviewer can reword it before saving. Nothing here writes the box: the
// surface owns its own draft state and its own Save button.
//
// The ONLY Firestore write in this file is the marker, and it happens AFTER the
// surface's own save has already committed. That ordering is the whole design —
// a note must not be marked for text the reviewer never saved.
//
// One file owns the append rule, the did-anything-survive test and the marker
// write so the three surfaces that offer Promote (job quick view, job editor,
// invoice editor) cannot drift apart.
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

// Note text only — no author suffix, no date, no photos. A blank line between
// blocks so the paragraph break survives whitespace-pre-line on the invoice, the
// PDF and the customer portal. Trailing whitespace on the existing text is
// trimmed first so two promotions in a row don't stack blank lines.
export const appendToWorkPerformed = (existing, noteText) => {
  const add = (noteText || '').trim();
  if (!add) return existing || '';
  const base = (existing || '').replace(/\s+$/, '');
  return base ? `${base}\n\n${add}` : add;
};

// wpauthor-5d3f8a26 — INVOICE PATH ONLY now. Its one remaining caller is
// InvoiceFormModal: invoice.workPerformed is still a single free-text box (an
// invoice is a frozen customer document, with no authorship), so the guess below
// is still the best available answer there.
//
// The JOB path retired it. Work Performed on a job is a list of authored entries,
// so "did the promoted text survive?" stopped being a guess — an entry document
// either got written or it did not, and the quick view marks the note iff it did.
//
// The two cases where we can be CERTAIN nothing the reviewer promoted survived
// to the save: the box ended up empty, or it ended up exactly as it was before
// the first promotion of this session.
//
// Rewording is indistinguishable from deleting — rewording IS editing the text
// we appended — so the bias is deliberately toward marking. An unmarked note
// invites the double promote the marker exists to prevent; a wrong tag is
// visible and clearable by the note's author or a high-level role.
export const promotionsSurvived = (prePromoteText, savedText) => {
  const saved = (savedText || '').trim();
  if (!saved) return false;
  return saved !== (prePromoteText || '').trim();
};

// Best-effort marker write. Never throws: the caller's save has already
// committed, so a failed tag must not look like a failed save.
//
// The five fields, the set-only boolean, the target type and the serverTimestamp
// are all pinned by firestore.rules isPromoteMarkerOnly() (wppromote-6a3f1e94),
// which is what lets Office Staff — who can edit Work Performed but is neither
// the note's author nor isHighLevel() — write this marker at all.
//
// currentUserProfile.id IS the Auth uid (ProtectedCrmLayout builds the profile
// from doc(db,'users',user.uid) with id: userDoc.id), so the rule's
// promotedByUid == request.auth.uid check needs no extra lookup here.
export const markNotesPromoted = async ({ orgId, noteIds, currentUserProfile, targetType }) => {
  if (!orgId || !currentUserProfile?.id) return;
  if (!Array.isArray(noteIds) || noteIds.length === 0) return;
  await Promise.all(noteIds.map(async (noteId) => {
    try {
      await updateDoc(doc(db, `organizations/${orgId}/notes`, noteId), {
        promotedToWorkPerformed: true,
        promotedTargetType: targetType,
        promotedAt: serverTimestamp(),
        promotedByUid: currentUserProfile.id,
        promotedByName: currentUserProfile.name || '',
      });
    } catch (err) {
      console.warn('promoteNote: marker write failed (the text is already saved)', noteId, err);
    }
  }));
};
